// Forward a message (ForwardMessageSheet.swift + ConversationTargets.swift, shared/API.md §31):
// pick a conversation — any team, most recently active first — add an optional comment, and the
// post is the same permalink-text post the desktop makes (ForwardPresentation.message). The web
// adds the webapp's "people" tier while filtering: someone you have no DM with yet is still a
// destination (the DM is opened first — people feature's openDirectChannel).
//
// ForwardHost is mounted once by the workspace; rows ask for it through composeSignals.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { displayName, userDisplayName } from '../../../core/channelListDerivation';
import * as ForwardPresentation from '../../../core/forwardPresentation';
import { InlineMarkdown } from '../../../core/inlineMarkdown';
import type { UserRecord } from '../../../core/records';
import type { ForwardData } from '../../../data/features/compose';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, Dialog, SearchInput } from '../../kit/Kit';
import { ShellContext } from '../../shell/ShellContext';
import { closeForward, useForwardRequest, type ForwardRequest } from './composeSignals';
import './compose.css';

const PEOPLE_DEBOUNCE_MS = 250;
const TOAST_MS = 6_000;

type Pick = { kind: 'conversation'; channelId: string; name: string } | { kind: 'person'; userId: string; name: string };

interface Toast {
  text: string;
  tone: 'info' | 'error';
  channelId?: string;
}

export function ForwardHost() {
  const request = useForwardRequest();
  const shell = useContext(ShellContext);
  const [toast, setToast] = useState<Toast | null>(null);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(id);
  }, [toast]);
  if (!shell) return null;
  return (
    <>
      {request ? (
        <ForwardDialog
          key={request.post.id}
          request={request}
          teamName={shell.team.name}
          currentTeamId={shell.team.id}
          onClose={closeForward}
          onResult={setToast}
        />
      ) : null}
      {toast ? (
        <div className={`compose-toast${toast.tone === 'error' ? ' error' : ''}`} role="status">
          <span>{toast.text}</span>
          {toast.channelId ? (
            <button
              type="button"
              onClick={() => {
                shell.openChannel(toast.channelId!);
                setToast(null);
              }}
            >
              {t('web.compose.forward.go')}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function ForwardDialog({
  request,
  teamName,
  currentTeamId,
  onClose,
  onResult,
}: {
  request: ForwardRequest;
  teamName: string;
  currentTeamId: string;
  onClose(): void;
  onResult(t: Toast): void;
}) {
  const [raw, setRaw] = useState<ForwardData | null>(null);
  const [filter, setFilter] = useState('');
  const [comment, setComment] = useState('');
  const [picked, setPicked] = useState<Pick | null>(null);
  const [highlight, setHighlight] = useState(0);
  const [people, setPeople] = useState<UserRecord[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  // Read from the store ONCE per opening; the filter runs over this list in memory.
  useEffect(() => {
    void data.call('forwardData').then(setRaw, () => setRaw({ currentUserId: '', channels: [], users: [], participants: {}, teams: [] }));
  }, []);

  const model = useMemo(() => {
    if (!raw) return null;
    const usersById = new Map(raw.users.map((u) => [u.id, u]));
    const names: Record<string, string> = {};
    const slugs: Record<string, string> = {};
    const dmPartner = new Map<string, string>();
    for (const c of raw.channels) {
      names[c.id] = displayName(c, {
        currentUserId: raw.currentUserId,
        usersById,
        participantIds: raw.participants[c.id] ?? [],
        unknownUserFallback: c.type === 'G' ? t('channelList.unknownGroup') : t('channelList.unknownUser'),
      });
      slugs[c.id] = c.name;
      if (c.type === 'D') {
        const [a, b] = c.name.split('__');
        dmPartner.set(a === raw.currentUserId ? b : a, c.id);
      }
    }
    const teamNames = Object.fromEntries(raw.teams.map((x) => [x.id, x.displayName]));
    const targets = ForwardPresentation.targets(raw.channels, names, teamNames, currentTeamId);
    return { targets, slugs, dmPartner, me: raw.currentUserId };
  }, [raw, currentTeamId]);

  // The people tier: only while filtering, store first then the server, never someone whose DM
  // is already a row (it would be the same destination twice).
  const term = filter.trim();
  useEffect(() => {
    if (!term) {
      setPeople([]);
      return;
    }
    let alive = true;
    void data.call('peopleLocal', term, 8).then((r) => alive && setPeople(r.users));
    const id = setTimeout(() => {
      void data.call('peopleSearch', currentTeamId, term).then((r) => {
        if (!alive || 'error' in r) return;
        setPeople((prev) => {
          const seen = new Set(prev.map((u) => u.id));
          return [...prev, ...r.users.filter((u) => !seen.has(u.id))];
        });
      });
    }, PEOPLE_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [term, currentTeamId]);

  const visible = useMemo(() => (model ? ForwardPresentation.filtered(model.targets, filter, model.slugs) : []), [model, filter]);
  const extraPeople = useMemo(() => {
    if (!model || !term) return [];
    const shown = new Set(visible.map((v) => v.channelId));
    return people
      .filter((u) => u.id !== model.me && u.deleteAt === 0)
      .filter((u) => {
        const dm = model.dmPartner.get(u.id);
        return !dm || !shown.has(dm);
      })
      .slice(0, 8);
  }, [people, model, term, visible]);

  const rows: Pick[] = useMemo(
    () => [
      ...visible.map((v) => ({ kind: 'conversation' as const, channelId: v.channelId, name: v.name })),
      ...extraPeople.map((u) => ({ kind: 'person' as const, userId: u.id, name: userDisplayName(u) })),
    ],
    [visible, extraPeople],
  );
  useEffect(() => setHighlight(0), [filter]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const same = (a: Pick | null, b: Pick) =>
    !!a && a.kind === b.kind && (a.kind === 'conversation' ? a.channelId === (b as { channelId: string }).channelId : a.userId === (b as { userId: string }).userId);

  const send = async (explicit?: Pick) => {
    const choice = explicit ?? picked;
    if (!choice) return;
    onClose();
    let channelId: string;
    if (choice.kind === 'person') {
      const opened = await data.call('openDirectChannel', [choice.userId]);
      if ('error' in opened) return onResult({ text: t('web.compose.forward.failed'), tone: 'error' });
      channelId = opened.channelId;
    } else channelId = choice.channelId;
    const r = await data.call('forwardPost', { targetChannelId: channelId, postId: request.post.id, comment, teamName });
    if (r.ok) onResult({ text: tf('web.compose.forward.sent', choice.name), tone: 'info', channelId });
    else onResult({ text: r.message || t('web.compose.forward.failed'), tone: 'error' });
  };

  const snippet = request.post.message.trim() ? InlineMarkdown.parse(request.post.message).text : t('web.compose.forward.attachmentOnly');

  return (
    <Dialog
      title={t('forward.title')}
      onClose={onClose}
      width={540}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="primary" icon="send" disabled={!picked} onClick={() => void send()}>
            {t('forward.send')}
          </Button>
        </>
      }
    >
      <div className="compose-forward-quote" aria-label={t('web.compose.forward.preview')}>
        <div className="who">{request.authorName}</div>
        <div className="what">{snippet}</div>
      </div>
      <textarea
        className="compose-forward-comment"
        rows={2}
        value={comment}
        placeholder={t('forward.comment')}
        aria-label={t('forward.comment')}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void send();
          }
        }}
      />
      <div
        onKeyDown={(e) => {
          if (!rows.length) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => (h + (e.key === 'ArrowDown' ? 1 : rows.length - 1)) % rows.length);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (e.metaKey || e.ctrlKey) void send();
            else setPicked(rows[Math.min(highlight, rows.length - 1)]);
          }
        }}
      >
        <SearchInput value={filter} onChange={setFilter} placeholder={t('forward.filter')} autoFocus />
      </div>
      <div className="compose-forward-list" ref={listRef} role="listbox" aria-label={t('forward.title')}>
        {!model ? null : rows.length === 0 ? (
          <div className="compose-forward-empty">{model.targets.length === 0 ? t('forward.empty') : t('forward.noMatches')}</div>
        ) : (
          rows.map((row, i) => {
            const header =
              i === 0 && row.kind === 'conversation' && !term ? (
                <div className="section">{t('web.compose.forward.recent')}</div>
              ) : row.kind === 'person' && (i === 0 || rows[i - 1].kind !== 'person') ? (
                <div className="section">{t('web.compose.forward.people')}</div>
              ) : null;
            const target = row.kind === 'conversation' ? visible[i] : null;
            const person = row.kind === 'person' ? extraPeople[i - visible.length] : null;
            const selected = same(picked, row);
            return (
              <div key={row.kind === 'conversation' ? row.channelId : 'u-' + row.userId}>
                {header}
                <button
                  type="button"
                  data-index={i}
                  className={`compose-forward-row${i === highlight ? ' highlight' : ''}`}
                  role="option"
                  aria-selected={selected}
                  onClick={() => setPicked(row)}
                  onDoubleClick={() => void send(row)}
                >
                  <span className="glyph">
                    {person ? (
                      <Avatar userId={person.id} name={row.name} size={24} version={person.lastPictureUpdate} />
                    ) : (
                      <Icon name={target?.type === 'P' ? 'lock' : target?.type === 'D' ? 'person' : target?.type === 'G' ? 'people' : 'hash'} size={16} />
                    )}
                  </span>
                  <span className="names">
                    <span className="name">{row.name}</span>
                    {target?.teamName ? <span className="team">{target.teamName}</span> : null}
                    {person ? <span className="team">@{person.username} · {t('web.compose.forward.directMessage')}</span> : null}
                  </span>
                  {selected ? <Icon name="check" size={16} className="tick" strokeWidth={2.2} /> : null}
                </button>
              </div>
            );
          })
        )}
      </div>
    </Dialog>
  );
}
